<script setup lang="ts">
import { ref } from 'vue';

// Demo data — Friday 3 Apr 2026: Cardiff Devils vs Nottingham Panthers
const score = ref({
  home: { name: 'CARDIFF DEVILS', short: 'DEVILS', goals: 2 },
  away: { name: 'NOTTINGHAM PANTHERS', short: 'PANTHERS', goals: 1 },
  period: 'P2',
  clock: '08:44',
  isLive: true,
});

const predictions = ref({ open: 4 });
const auction = ref({ live: 3 });
const draw = ref({ pot: 3240, tickets: 1842 });

const leaderboard = ref([
  { rank: 1, name: 'DevilsJon99', pts: 1840 },
  { rank: 2, name: 'KatieRinkside', pts: 1540 },
  { rank: 3, name: 'TheHolmes', pts: 1210 },
]);

const you = ref({ rank: 6, name: 'IceQueenCardiff', pts: 620 });
</script>

<template>
  <div class="shell">

    <!-- ═══ APP HEADER ═══ -->
    <header class="app-header">
      <div class="header-left">
        <!-- Cardiff Devils logo -->
        <img src="/devils-logo.png" alt="Cardiff Devils" class="logo-img" />
        <div class="brand-text">
          <span class="brand-name">CARDIFF <span class="brand-accent">/</span> DEVILS</span>
          <span class="brand-sub">ICE MASTER</span>
        </div>
      </div>
      <!-- LIVE pill -->
      <div v-if="score.isLive" class="live-pill">
        <span class="live-dot">
          <span class="live-ping"></span>
          <span class="live-core"></span>
        </span>
        <span class="live-text">LIVE</span>
      </div>
    </header>

    <!-- ═══ BRAND STRIPE ═══ -->
    <div class="brand-stripe"></div>

    <!-- ═══ SCROLLABLE CONTENT ═══ -->
    <div class="content">

      <!-- ── SCORE CHIP ── -->
      <div class="score-chip">
        <div class="score-badge">
          <span class="score-badge-text">{{ score.period }} &bull; {{ score.clock }}</span>
        </div>
        <div class="score-row">
          <div class="score-team home">
            <span class="team-name home-name">{{ score.home.short }}</span>
          </div>
          <div class="score-numbers">
            <span class="score-digit home-digit">{{ score.home.goals }}</span>
            <span class="score-dash">&mdash;</span>
            <span class="score-digit away-digit">{{ score.away.goals }}</span>
          </div>
          <div class="score-team away">
            <span class="team-name away-name">{{ score.away.short }}</span>
          </div>
        </div>
        <div class="score-full-names">
          <span>{{ score.home.name }}</span>
          <span>{{ score.away.name }}</span>
        </div>
      </div>

      <!-- ── ACTION GRID ── -->
      <div class="action-grid">
        <!-- Predict -->
        <router-link to="/predictions" class="tile r">
          <div class="tile-top">
            <svg class="tile-icon" viewBox="0 0 40 40" fill="none" width="40" height="40">
              <path d="M20 4L24 16H36L26 23L30 35L20 27L10 35L14 23L4 16H16L20 4Z" fill="var(--red)" opacity=".15"/>
              <path d="M20 6l3 10h10l-8 6 3 10-8-6-8 6 3-10-8-6h10z" stroke="var(--red)" stroke-width="1.5" fill="none"/>
              <path d="M18 18l2-6 2 6" stroke="var(--rh)" stroke-width="2" stroke-linecap="round"/>
              <circle cx="20" cy="14" r="1.5" fill="var(--rh)"/>
            </svg>
            <span class="tile-badge r-badge">{{ predictions.open }} open</span>
          </div>
          <span class="tile-title">PREDICT</span>
          <span class="tile-sub">{{ predictions.open }} open now</span>
        </router-link>

        <!-- Auction -->
        <router-link to="/auctions" class="tile g-gold">
          <div class="tile-top">
            <svg class="tile-icon" viewBox="0 0 40 40" fill="none" width="40" height="40">
              <rect x="6" y="32" width="28" height="3" rx="1.5" fill="var(--gld)" opacity=".25"/>
              <path d="M24 10l8 8-14 14-8-8z" stroke="var(--gld)" stroke-width="1.5" fill="none"/>
              <path d="M28 6l6 6" stroke="var(--gi)" stroke-width="2.5" stroke-linecap="round"/>
            </svg>
            <span class="tile-badge g-badge">{{ auction.live }} live</span>
          </div>
          <span class="tile-title">AUCTION</span>
          <span class="tile-sub">{{ auction.live }} live lots</span>
        </router-link>

        <!-- 50/50 Draw -->
        <router-link to="/draw" class="tile g-green">
          <svg viewBox="0 0 36 36" fill="none" width="36" height="36" style="flex-shrink:0">
            <rect x="3" y="7" width="30" height="22" rx="4" stroke="var(--grn)" stroke-width="1.5" fill="none"/>
            <path d="M3 14h30" stroke="var(--grn)" stroke-width="1" opacity=".4"/>
            <circle cx="11" cy="23" r="2" fill="var(--gh)"/><circle cx="18" cy="23" r="2" fill="var(--gh)"/><circle cx="25" cy="23" r="2" fill="var(--gh)"/>
          </svg>
          <span class="tile-title">50/50 DRAW</span>
          <span class="tile-sub" style="color:var(--gi);font-family:var(--FD);font-size:22px">&pound;{{ draw.pot.toLocaleString() }}</span>
          <span class="tile-sub">{{ draw.tickets.toLocaleString() }} tickets</span>
        </router-link>

        <!-- Shirt Raffle -->
        <router-link to="/draw" class="tile g-red">
          <svg viewBox="0 0 36 36" fill="none" width="36" height="36" style="flex-shrink:0">
            <path d="M10 6L6 12v4h6v14h12V16h6v-4l-4-6h-6l-2 3-2-3z" stroke="var(--red)" stroke-width="1.5" fill="none"/>
            <text x="18" y="24" text-anchor="middle" fill="var(--rh)" font-size="8" font-weight="bold" font-family="var(--FD)">17</text>
          </svg>
          <span class="tile-title">SHIRT RAFFLE</span>
          <span class="tile-sub" style="color:var(--rh);font-family:var(--FU);font-size:13px;font-weight:700">COX #17 JERSEY</span>
          <span class="tile-sub">487 / 500 tickets</span>
        </router-link>
      </div>

      <!-- ── INTERMISSION GAMES ── -->
      <div class="card games-card">
        <div class="card-head">
          <span class="card-title">INTERMISSION GAMES</span>
          <span class="game-live-badge">LIVE NOW</span>
        </div>
        <p class="games-sub">Available during intermissions &mdash; compete for tonight's leaderboard</p>
        <div class="games-grid">
          <router-link to="/games/shootout" class="game-tile">
            <svg viewBox="0 0 32 32" width="32" height="32" fill="none" style="margin-bottom:6px">
              <circle cx="16" cy="16" r="11" stroke="var(--red)" stroke-width="2" fill="none"/>
              <circle cx="16" cy="16" r="4" fill="var(--red)"/>
              <path d="M10 8L22 24M22 8L10 24" stroke="var(--red)" stroke-width="1.5" opacity=".3"/>
            </svg>
            <span class="game-name">Puck Shootout</span>
            <span class="game-desc">Flick to score</span>
          </router-link>
          <router-link to="/games/goalie" class="game-tile">
            <svg viewBox="0 0 32 32" width="32" height="32" fill="none" style="margin-bottom:6px">
              <rect x="6" y="8" width="20" height="18" rx="4" stroke="var(--grn)" stroke-width="2" fill="none"/>
              <path d="M6 14h20" stroke="var(--grn)" stroke-width="1.5" opacity=".4"/>
              <path d="M12 20h8" stroke="var(--gh)" stroke-width="2" stroke-linecap="round"/>
              <circle cx="16" cy="6" r="4" stroke="var(--grn)" stroke-width="1.5" fill="none"/>
            </svg>
            <span class="game-name">Goalie Mode</span>
            <span class="game-desc">Save the shots</span>
          </router-link>
        </div>
        <!-- Fan Cam link -->
        <router-link to="/fan-cam" class="fancam-link">
          <svg viewBox="0 0 28 28" fill="none" width="24" height="24" style="flex-shrink:0">
            <rect x="3" y="7" width="22" height="16" rx="3" stroke="var(--red)" stroke-width="1.5" fill="none"/>
            <circle cx="14" cy="15" r="4.5" stroke="var(--red)" stroke-width="1.5" fill="none"/>
            <circle cx="14" cy="15" r="1.5" fill="var(--red)" opacity=".3"/>
            <rect x="9" y="4" width="10" height="4" rx="1.5" stroke="var(--red)" stroke-width="1" fill="none"/>
          </svg>
          <div class="fancam-link-text">
            <span class="fancam-link-title">FAN CAM</span>
            <span class="fancam-link-sub">Get on the big screen!</span>
          </div>
          <svg viewBox="0 0 20 20" fill="none" width="16" height="16" style="flex-shrink:0;color:var(--dim)">
            <path d="M7 4l6 6-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </router-link>
      </div>

      <!-- ── ORDER / MERCH ── -->
      <div class="order-grid">
        <router-link to="/food" class="order-tile order-tile-link">
          <svg viewBox="0 0 32 32" width="28" height="28" fill="none" style="margin-bottom:6px">
            <rect x="5" y="12" width="22" height="14" rx="3" stroke="var(--grn)" stroke-width="1.8" fill="none"/>
            <path d="M9 12V9a7 7 0 0114 0v3" stroke="var(--gh)" stroke-width="1.8" fill="none"/>
            <circle cx="16" cy="19" r="2.5" fill="var(--grn)"/>
            <path d="M8 26h16" stroke="var(--grn)" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          <span class="order-name">ORDER FOOD &amp; DRINK</span>
        </router-link>
        <router-link to="/shop" class="order-tile order-tile-link">
          <svg viewBox="0 0 32 32" width="28" height="28" fill="none" style="margin-bottom:6px">
            <path d="M10 6L6 12v4h6v14h8V16h6v-4l-4-6h-6l-2 3-2-3z" stroke="var(--red)" stroke-width="1.8" fill="none"/>
            <text x="16" y="24" text-anchor="middle" fill="var(--rh)" font-size="7" font-weight="bold" style="font-family:var(--FD)">17</text>
          </svg>
          <span class="order-name">MERCH SHOP</span>
        </router-link>
      </div>

      <!-- ── MINI LEADERBOARD ── -->
      <div class="card leaderboard">
        <div class="card-head">
          <span class="card-title">LEADERBOARD</span>
          <router-link to="/leaderboard" class="card-link">VIEW ALL</router-link>
        </div>
        <div class="lb-list">
          <div
            v-for="fan in leaderboard"
            :key="fan.rank"
            class="lb-row"
          >
            <span
              class="lb-rank"
              :style="{ color: fan.rank === 1 ? 'var(--gi)' : fan.rank === 2 ? '#C0C0C0' : '#CD7F32' }"
            >{{ fan.rank }}</span>
            <span
              class="lb-avatar"
              :style="{
                background: fan.rank === 1 ? 'rgba(240,192,64,.15)' : fan.rank === 2 ? 'rgba(192,192,192,.15)' : 'rgba(205,127,50,.15)',
                color: fan.rank === 1 ? 'var(--gi)' : fan.rank === 2 ? '#C0C0C0' : '#CD7F32',
              }"
            >{{ fan.name.charAt(0) }}</span>
            <span class="lb-name">{{ fan.name }}</span>
            <span class="lb-pts">{{ fan.pts.toLocaleString() }}</span>
          </div>

          <!-- Divider -->
          <div class="lb-divider"></div>

          <!-- You -->
          <div class="lb-row you-row">
            <span class="lb-rank" style="color: var(--mid)">{{ you.rank }}</span>
            <span class="lb-avatar" style="background: var(--rg); color: var(--red);">{{ you.name.charAt(0) }}</span>
            <span class="lb-name you-name">{{ you.name }}</span>
            <span class="lb-pts you-pts">{{ you.pts.toLocaleString() }}</span>
          </div>
        </div>
      </div>

      <!-- ── FOOTER ── -->
      <div class="footer">
        <span class="footer-text">Powered by <strong>Ice Master</strong></span>
      </div>

    </div>
  </div>
</template>

<style scoped>
/* ── SHELL ── */
.shell {
  max-width: 480px;
  height: 100vh;
  height: 100dvh;
  margin: 0 auto;
  background: var(--bg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

/* ── STATUS BAR ── */
.status-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 20px 4px;
  flex-shrink: 0;
}
.status-time {
  font-family: var(--FB);
  font-size: 14px;
  font-weight: 600;
  color: var(--wht);
}
.status-icons {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* ── APP HEADER ── */
.app-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  flex-shrink: 0;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.logo-img {
  width: 42px;
  height: 42px;
  object-fit: contain;
  filter: drop-shadow(0 0 8px rgba(204,0,0,.6));
}
.brand-text {
  display: flex;
  flex-direction: column;
}
.brand-name {
  font-family: var(--FD);
  font-size: 22px;
  color: var(--wht);
  letter-spacing: .06em;
  line-height: 1;
}
.brand-accent {
  color: var(--red);
  margin: 0 2px;
}
.brand-sub {
  font-family: var(--FU);
  font-weight: 700;
  font-size: 13px;
  letter-spacing: .16em;
  text-transform: uppercase;
  color: var(--dim);
  margin-top: 2px;
}

/* ── LIVE PILL ── */
.live-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 100px;
  background: rgba(204,0,0,.12);
  border: 1px solid rgba(204,0,0,.3);
}
.live-dot {
  position: relative;
  width: 8px;
  height: 8px;
}
.live-ping {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--rh);
  animation: ping 1.5s cubic-bezier(0,.4,.6,1) infinite;
}
.live-core {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--red);
}
.live-text {
  font-family: var(--FU);
  font-weight: 800;
  font-size: 15px;
  letter-spacing: .08em;
  color: var(--rh);
}
@keyframes ping {
  0% { transform: scale(1); opacity: .75; }
  75%, 100% { transform: scale(2.2); opacity: 0; }
}

/* ── BRAND STRIPE ── */
.brand-stripe {
  height: 4px;
  flex-shrink: 0;
  background: linear-gradient(180deg, var(--grn) 0%, var(--grn) 22%, var(--red) 22%, var(--red) 78%, var(--grn) 78%);
}

/* ── SCROLLABLE CONTENT ── */
.content {
  flex: 1;
  overflow-y: auto;
  padding: 20px 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── SCORE CHIP ── */
.score-chip {
  background: linear-gradient(135deg, var(--s1) 0%, var(--s2) 100%);
  border: 1px solid rgba(255,255,255,.05);
  border-radius: 20px;
  padding: 20px 16px 16px;
  text-align: center;
}
.score-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--s3);
  border-radius: 100px;
  padding: 4px 14px;
  margin-bottom: 8px;
}
.score-badge-text {
  font-family: var(--FU);
  font-weight: 700;
  font-size: 14px;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--mid);
}
.score-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.score-team {
  flex: 1;
  display: flex;
  justify-content: center;
}
.team-name {
  font-family: var(--FU);
  font-weight: 800;
  font-size: 18px;
  letter-spacing: .06em;
  text-transform: uppercase;
}
.home-name { color: var(--red); }
.away-name { color: var(--mid); }
.score-numbers {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.score-digit {
  font-family: var(--FD);
  font-size: 72px;
  line-height: .85;
}
.home-digit { color: var(--wht); }
.away-digit { color: var(--mid); }
.score-dash {
  font-family: var(--FD);
  font-size: 40px;
  color: var(--dim);
}
.score-full-names {
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  font-family: var(--FU);
  font-weight: 600;
  font-size: 13px;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--dim);
}

/* ── ACTION GRID ── */
.action-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

/* ── TILE BASE ── */
.tile {
  background: var(--s1);
  border: 1px solid rgba(255,255,255,.05);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  transition: transform .15s ease;
  text-decoration: none;
  color: var(--wht);
}
.tile:active { transform: scale(.97); }

/* Accent bar at bottom */
.tile::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 16px;
  right: 16px;
  height: 3px;
  border-radius: 3px 3px 0 0;
}
.tile.r::after { background: var(--red); }
.tile.g-red::after { background: var(--red); }
.tile.g-gold::after { background: var(--gld); }
.tile.g-green::after { background: var(--grn); }

.tile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.tile-icon {
  flex-shrink: 0;
}
.tile-badge {
  font-family: var(--FU);
  font-weight: 700;
  font-size: 13px;
  letter-spacing: .04em;
  padding: 3px 10px;
  border-radius: 100px;
}
.r-badge {
  background: rgba(204,0,0,.15);
  color: var(--rh);
}
.g-badge {
  background: rgba(201,168,76,.15);
  color: var(--gi);
}
.tile-title {
  font-family: var(--FD);
  font-size: 28px;
  line-height: 1;
  color: var(--wht);
}
.tile-sub {
  font-family: var(--FB);
  font-size: 14px;
  color: var(--dim);
  margin-top: 2px;
}

/* Wide tile */
.tile.wide {
  grid-column: 1 / -1;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, rgba(0,165,80,.08) 0%, var(--s1) 100%);
  border-color: rgba(0,165,80,.2);
}
.tile.wide::after {
  left: 16px;
  right: 16px;
}
.tile-wide-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.tile-wide-text {
  display: flex;
  flex-direction: column;
}
.tile-wide-right {
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.draw-pot {
  font-family: var(--FD);
  font-size: 32px;
  color: var(--gh);
  line-height: 1;
}
.draw-label {
  font-family: var(--FU);
  font-weight: 700;
  font-size: 12px;
  letter-spacing: .14em;
  color: var(--dim);
  margin-top: 4px;
}

/* ── CARD ── */
.card {
  background: var(--s1);
  border: 1px solid rgba(255,255,255,.05);
  border-radius: 16px;
  padding: 16px;
}
.card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}
.card-title {
  font-family: var(--FU);
  font-weight: 800;
  font-size: 18px;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--wht);
}
.card-link {
  font-family: var(--FU);
  font-weight: 700;
  font-size: 13px;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--gld);
  cursor: pointer;
  text-decoration: none;
}

/* ── LEADERBOARD ── */
.lb-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.lb-row {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--s2);
  border-radius: 12px;
  padding: 10px 12px;
}
.lb-rank {
  font-family: var(--FM);
  font-size: 15px;
  font-weight: 500;
  width: 20px;
  text-align: center;
}
.lb-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--FU);
  font-weight: 800;
  font-size: 14px;
  flex-shrink: 0;
}
.lb-name {
  flex: 1;
  font-family: var(--FB);
  font-size: 14px;
  color: var(--wht);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.lb-pts {
  font-family: var(--FM);
  font-size: 15px;
  font-weight: 500;
  color: var(--gld);
  font-variant-numeric: tabular-nums;
}

/* Divider */
.lb-divider {
  border-top: 1px dashed rgba(255,255,255,.06);
  margin: 2px 0;
}

/* You row */
.you-row {
  background: rgba(204,0,0,.06);
  border: 1px solid rgba(204,0,0,.15);
}
.you-name { font-weight: 600; }
.you-pts { color: var(--red); }

/* ── GAMES CARD ── */
.games-card {
  background: linear-gradient(135deg, rgba(201,168,76,.06) 0%, var(--s1) 100%);
  border-color: rgba(201,168,76,.15);
}
.game-live-badge {
  font-family: var(--FU);
  font-weight: 700;
  font-size: 12px;
  letter-spacing: .06em;
  padding: 3px 10px;
  border-radius: 100px;
  background: rgba(0,165,80,.15);
  color: var(--gh);
}
.games-sub {
  font-family: var(--FB);
  font-size: 14px;
  color: var(--dim);
  margin-bottom: 12px;
}
.games-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.game-tile {
  background: var(--s2);
  border: 1px solid rgba(255,255,255,.05);
  border-radius: 12px;
  padding: 14px;
  text-align: center;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: transform .15s ease;
}
.game-tile:active { transform: scale(.97); }
.game-name {
  font-family: var(--FU);
  font-weight: 700;
  font-size: 15px;
  color: var(--wht);
}
.game-desc {
  font-family: var(--FB);
  font-size: 13px;
  color: var(--dim);
  margin-top: 2px;
}

/* ── FAN CAM LINK ── */
.fancam-link {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(204,0,0,.06);
  border: 1px solid rgba(204,0,0,.18);
  text-decoration: none;
  color: inherit;
  transition: transform .15s ease;
}
.fancam-link:active { transform: scale(.97); }
.fancam-link-text {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.fancam-link-title {
  font-family: var(--FU);
  font-weight: 800;
  font-size: 16px;
  letter-spacing: .06em;
  color: var(--wht);
}
.fancam-link-sub {
  font-family: var(--FB);
  font-size: 13px;
  color: var(--dim);
  margin-top: 1px;
}

/* ── ORDER / MERCH ── */
.order-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.order-tile {
  background: var(--s1);
  border: 1px solid rgba(255,255,255,.05);
  border-radius: 16px;
  padding: 16px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.order-name {
  font-family: var(--FU);
  font-weight: 700;
  font-size: 14px;
  letter-spacing: .04em;
  color: var(--wht);
  margin-bottom: 6px;
}
.order-tile-link {
  text-decoration: none;
  color: inherit;
  transition: transform .15s ease;
  cursor: pointer;
}
.order-tile-link:active { transform: scale(.97); }

/* ── FOOTER ── */
.footer {
  text-align: center;
  padding: 12px 0 4px;
}
.footer-text {
  font-family: var(--FU);
  font-size: 12px;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: var(--dim);
}
.footer-text strong {
  color: var(--mid);
  font-weight: 700;
}
</style>
